<script lang="ts" setup>
import type { BlogCollectionItem } from '@nuxt/content';

import hydrationGuidePdf from '~/assets/guides/HYDRATION_GUIDE.pdf?url';
import retinoidsGuidePdf from '~/assets/guides/RETINAL_GUIDE.pdf?url';
import careGuidePdf from '~/assets/guides/SKIN_CARE_GUIDE.pdf?url';
import wasteGuidePdf from '~/assets/guides/WASTE_GUIDE.pdf?url';

const props = defineProps<{
  article: Pick<BlogCollectionItem, 'path' | 'image' | 'category' | 'title' | 'downloadUrl'>;
  large?: boolean;
  horizontal?: boolean;
}>();

const { t } = useI18n();

const DOCUMENT_MAP: Record<string, string> = {
  retinal: retinoidsGuidePdf,
  hydration: hydrationGuidePdf,
  waste: wasteGuidePdf,
  care: careGuidePdf,
};

const link = computed(() => {
  if (props.article.downloadUrl) {
    if (/^\w+$/.test(props.article.downloadUrl) && props.article.downloadUrl in DOCUMENT_MAP)
      return DOCUMENT_MAP[props.article.downloadUrl];
    return props.article.downloadUrl;
  }
  return articleLink(props.article.path);
});

const currentCategory = computed(() => BLOG_CATEGORIES.find((el) => el.code === props.article.category));

const isGuide = computed(() => !!props.article.downloadUrl);
</script>

<template>
  <div
    :key="article.path"
    class="blogCard"
    :class="{
      blogCard_large: large,
      blogCard_horizontal: horizontal,
      blogCard_guide: isGuide,
    }"
  >
    <NuxtLink v-if="!isGuide" :to="link" :aria-label="t('navigate')" class="blogCard__navigate" />

    <div class="blogCard__wrapper">
      <NuxtImg
        :src="article.image.src"
        :sizes="large ? IMAGE_SIZES.blogLarge : IMAGE_SIZES.blogSmall"
        :alt="article.image.alt"
        format="webp"
        class="blogCard__image"
      />

      <a
        :href="link"
        :download="isGuide ? true : undefined"
        :target="isGuide ? '_blank' : ''"
        class="blogCard__download"
      >
        {{ t(isGuide ? 'download' : 'read') }}
      </a>
    </div>

    <div class="blogCard__info">
      <span v-if="currentCategory" class="blogCard__label" :style="{ color: currentCategory.color }">
        {{ t(`blog.category.${currentCategory.code}`) }}
      </span>
      <p class="blogCard__title">
        {{ article.title }}
      </p>
    </div>
  </div>
</template>

<i18n src="~/locales/blog.yaml"></i18n>
<i18n>
ru:
  download: Скачать
  read: Читать
  navigate: Перейти

en:
  download: Download
  read: Read
  navigate: Open
</i18n>

<style lang="scss">
.blogCard {
  $self: &;

  position: relative;
  display: flex;
  flex-direction: column;
  gap: 20px;

  @include mq('sm') {
    gap: 16px;
  }

  &_guide {
    cursor: default;
  }

  @include mq('none', 'lg') {
    &_large {
      grid-column: span 2;
      grid-row: span 2;

      #{$self}__wrapper {
        aspect-ratio: 689 / 548;
      }

      #{$self}__title {
        font-size: 34px;
        line-height: 40px;
      }
    }

    &_horizontal {
      flex-direction: row;
      grid-column: span 2;

      #{$self}__wrapper,
      #{$self}__info {
        width: calc((100% - 20px) / 2);
      }

      #{$self}__download {
        padding: 10px 25px;
        font-size: 13px;
      }
    }
  }

  &__wrapper {
    position: relative;
    width: 100%;
    aspect-ratio: 1 / 1;
    pointer-events: none;
  }

  &__image {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
    border-radius: 10px;
  }

  &__download {
    position: absolute;
    bottom: 20px;
    left: 20px;

    display: flex;
    align-items: center;
    gap: 8px;

    padding: 12px 40px;
    background: #ffffff;
    border-radius: 30px;

    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    color: #121212;
    text-decoration: none;

    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;
    cursor: pointer;
    pointer-events: auto;

    @include mq('sm') {
      bottom: 16px;
      padding: 10px 20px;
      font-size: 13px;
    }

    &:hover {
      background: #f5f5f5;
    }
  }

  &__info {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  &__label {
    width: 148px;
    height: 32px;
    font-size: 14px;
    font-weight: 600;
    display: flex;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: #ffffff;
    margin-bottom: 8px;
  }

  &__title {
    font-size: 18px;
    line-height: 24px;
    letter-spacing: 0.2px;
    color: #121212;
    margin: 0;
  }

  &__navigate {
    position: absolute;
    inset: 0;
    border-radius: inherit;
  }
}
</style>
